import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import isEqual from 'fast-deep-equal'
import * as Y from 'yjs'

import { useGetRecord } from 'data/hooks/records'
import {
    deleteRecord as deleteRecordOperation,
    invalidateRecord,
    updateRecord,
} from 'data/hooks/records/recordOperations'
import { useYjsState } from 'features/utils/useYjsState'
import {
    isUsersRecord,
    useUserRecordLinksAvatars,
} from 'features/views/attributes/hooks/useUserRecordLinksAvatars'
import { useStackerUsersObject } from 'features/workspace/stackerUserUtils'

import useDebounce from 'v2/ui/utils/useDebounce'
import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { useToast } from 'ui/components/Toast'

const LOADING_SLOW_THRESHOLD_TIMEOUT = 2000
const VALUE_SYNC_DEBOUNCE_TIME = 200
const SAVE_CHANGES_DEBOUNCE_TIME = 200

export type RecordManager = {
    record?: RecordDto
    includeFields?: string[]
    dereferencedRecords?: RecordDto[]
    permissions: {
        canDelete: boolean
        canUpdateFields: string[]
        canReadFields: string[]
    }
    isLoading: boolean
    isFetchingSlow: boolean
    isError: boolean
    isNotFoundError: boolean
    isSaving: boolean
    mutateRecord: (tr: (record: Y.Map<any>) => void) => void
    replaceAttribute: (fieldApiName: string, value: Y.AbstractType<any>) => void
    mutateAttribute: (
        fieldApiName: string,
        tr: (value: Y.AbstractType<any>) => Y.AbstractType<any> | undefined
    ) => void
    clearAttribute: (fieldApiName: string) => void
    discardChanges: () => void
    deleteRecord: () => void
    saveChanges: (saveOptions?: {
        successToast?: {
            resolveFieldLabel?: (fieldApiName: string) => string | undefined
        }
    }) => Promise<void>
    refetch: () => Promise<void>
    isDirty: boolean
}

type UseRecordManagerStateOptions = {
    recordSid?: string
    includeFields?: string[]
    showSuccessToast?: boolean
}

export function useRecordManagerState(options: UseRecordManagerStateOptions): RecordManager {
    const { recordSid, includeFields, showSuccessToast } = options
    const showSuccessToastRef = useRef(showSuccessToast)
    showSuccessToastRef.current = showSuccessToast

    // Store included fields as a string to avoid unnecessary re-renders.
    const includedFieldsStr = includeFields?.join(',')
    const [includedFields, setIncludedFields] = useState(
        getIncludedFieldsFromString(includedFieldsStr)
    )
    const includeFieldsRef = useRef(includedFields)
    includeFieldsRef.current = includedFields
    useEffect(() => {
        setIncludedFields(getIncludedFieldsFromString(includedFieldsStr))
    }, [includedFieldsStr])

    const {
        data: originalRecord,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetRecord({
        recordId: recordSid!,
        options: { dereference: true },
        useQueryOptions: {
            refetchOnMount: false,
            refetchOnWindowFocus: true,
            enabled: !!recordSid,
        },
        includeFields: includedFields,
    })
    const originalRecordMemo = useDeepEqualsMemoValue(originalRecord)
    const originalRecordRef = useRef(originalRecordMemo)
    originalRecordRef.current = originalRecordMemo

    // Implement our own refetch on mount, since the react-query one is unreliable.
    useLayoutEffect(() => {
        if (!!recordSid) {
            invalidateRecord(recordSid)
        }
    }, [recordSid])

    const isNotFoundError =
        typeof error === 'object' && !!error && 'status' in error && error.status === 404

    const {
        data: record,
        replaceValue,
        applyTransaction,
    } = useYjsState<RecordDto>(originalRecordMemo ?? ({} as RecordDto))
    const updatedRecord = !!originalRecord && Object.values(record).length > 0 ? record : undefined
    const updatedRecordRef = useRef(updatedRecord)
    updatedRecordRef.current = updatedRecord

    const recordDiff = getRecordDiff(originalRecord, updatedRecord)
    const recordDiffRef = useRef(recordDiff)
    recordDiffRef.current = recordDiff

    const isDirty = Object.keys(recordDiff).length > 0
    const isDirtyRef = useRef(isDirty)
    isDirtyRef.current = isDirty

    const debouncedReplaceValue = useDebounce(replaceValue, VALUE_SYNC_DEBOUNCE_TIME)
    // Sync the record with the original record when the original record changes.
    useEffect(() => {
        debouncedReplaceValue(originalRecordMemo ?? ({} as RecordDto))
    }, [originalRecordMemo, debouncedReplaceValue])

    const [isFetchingSlow, setIsFetchingSlow] = useState(false)
    useEffect(() => {
        let timer: number

        if (isFetching) {
            timer = window.setTimeout(() => {
                setIsFetchingSlow(true)
            }, LOADING_SLOW_THRESHOLD_TIMEOUT)
        } else {
            setIsFetchingSlow(false)
        }

        return () => {
            window.clearTimeout(timer)
        }
    }, [isFetching])

    const isLoading = !originalRecord

    const replaceAttribute: RecordManager['replaceAttribute'] = useCallback(
        (fieldApiName, value) => {
            applyTransaction((record) => {
                record.set(fieldApiName, value)
            })
        },
        [applyTransaction]
    )

    const mutateAttribute: RecordManager['mutateAttribute'] = useCallback(
        (fieldApiName, tr) => {
            applyTransaction((record) => {
                const value = record.get(fieldApiName)
                const replaceValue = tr(value)
                record.set(fieldApiName, replaceValue)
            })
        },
        [applyTransaction]
    )

    const clearAttribute: RecordManager['clearAttribute'] = useCallback(
        (fieldApiName) => {
            applyTransaction((record) => {
                record.set(fieldApiName, null)
            })
        },
        [applyTransaction]
    )

    const [isSaving, setIsSaving] = useState(false)
    const toast = useToast()

    const handleSuccessToast = useCallback(
        (
            originalRecord: RecordDto,
            updatedRecord: RecordDto,
            saveOptions?: Parameters<RecordManager['saveChanges']>[0]
        ) => {
            const invertedDiff = getRecordDiff(updatedRecord, originalRecord)

            const diffApiNames = Object.keys(invertedDiff)
            const updatedFieldCount = diffApiNames.length

            if (!showSuccessToastRef.current || updatedFieldCount < 1) return

            const updatedFieldLabel = updatedFieldCount === 1 ? 'field' : 'fields'
            let title = `${updatedFieldCount} ${updatedFieldLabel} updated successfully`
            let revertTitle = `${updatedFieldCount} ${updatedFieldLabel} reverted`

            // When a resolver is provided, we only want to show the toast for single field updates.
            if (
                updatedFieldCount === 1 &&
                typeof saveOptions?.successToast?.resolveFieldLabel === 'function'
            ) {
                const fieldLabel = saveOptions.successToast.resolveFieldLabel(diffApiNames[0])
                if (fieldLabel) {
                    title = `${fieldLabel} updated successfully`
                    revertTitle = `${fieldLabel} reverted`
                }
            }

            const dismissSuccessToast = toast({
                type: 'success',
                startIcon: {
                    name: 'CheckCircle',
                },
                title,
                helperText: 'Click to revert changes',
                showDismissButton: true,
                onClick: async () => {
                    dismissSuccessToast()

                    try {
                        setIsSaving(true)

                        await updateRecord(
                            updatedRecord._sid,
                            invertedDiff,
                            includeFieldsRef.current,
                            { deferStoreUpdate: true }
                        )
                        invalidateRecord(updatedRecord._sid)

                        toast({
                            type: 'default',
                            startIcon: {
                                name: 'Info',
                            },
                            title: revertTitle,
                            showDismissButton: true,
                        })
                    } catch {
                        toast({
                            type: 'error',
                            startIcon: {
                                name: 'AlertCircle',
                            },
                            title: 'There was a problem reverting changes',
                            helperText: 'If the issue persists, contact support.',
                        })
                    } finally {
                        setIsSaving(false)
                    }
                },
            })
        },
        [toast]
    )

    const saveChanges: RecordManager['saveChanges'] = useCallback(
        async (saveOptions) => {
            // Only update the record if it has changed.
            const isDirty = isDirtyRef.current
            if (!isDirty) return Promise.resolve()

            const updatedRecord = updatedRecordRef.current
            const originalRecord = originalRecordRef.current
            if (!updatedRecord || !originalRecord) return Promise.resolve()

            try {
                setIsSaving(true)

                const diff = recordDiffRef.current

                await updateRecord(updatedRecord._sid, diff, includeFieldsRef.current, {
                    deferStoreUpdate: true,
                })
                invalidateRecord(updatedRecord._sid)

                handleSuccessToast(originalRecord, updatedRecord, saveOptions)
            } catch {
                toast({
                    type: 'error',
                    startIcon: {
                        name: 'AlertCircle',
                    },
                    title: 'There was a problem saving the record',
                    helperText: 'Please try again later. If the issue persists, contact support.',
                })
            } finally {
                setIsSaving(false)
            }
        },
        [handleSuccessToast, toast]
    )
    const debouncedSaveChanges = useDebounce(saveChanges, SAVE_CHANGES_DEBOUNCE_TIME)

    const discardChanges: RecordManager['discardChanges'] = useCallback(() => {
        const originalRecord = originalRecordRef.current
        replaceValue(originalRecord ?? ({} as RecordDto))
    }, [replaceValue])

    const deleteRecord: RecordManager['deleteRecord'] = useCallback(() => {
        if (!recordSid) return

        deleteRecordOperation(recordSid)
    }, [recordSid])

    const dereferencedRecords = useDereferencedRecords(originalRecord)

    const permissions = {
        canDelete: originalRecord?._permissions?.may_delete ?? false,
        canUpdateFields: originalRecord?._permissions?.may_update_fields ?? [],
        canReadFields: originalRecord?._permissions?.may_read_fields ?? [],
    }
    if (!originalRecord?._permissions?.may_update) {
        permissions.canUpdateFields = []
    }

    return useDeepEqualsMemoValue({
        record: updatedRecord,
        isLoading,
        isFetchingSlow,
        isError,
        isNotFoundError,
        mutateRecord: applyTransaction,
        replaceAttribute,
        mutateAttribute,
        clearAttribute,
        isSaving,
        saveChanges: debouncedSaveChanges,
        discardChanges,
        deleteRecord,
        isDirty,
        dereferencedRecords,
        permissions,
        includeFields: includedFields,
        refetch: refetch as unknown as RecordManager['refetch'],
    })
}

function getRecordDiff(originalRecord?: RecordDto, updatedRecord?: RecordDto): Partial<RecordDto> {
    if (!originalRecord || !updatedRecord) return {}

    const diff: Partial<RecordDto> = {}

    // Get all keys from both records
    const allKeys = new Set([...Object.keys(originalRecord), ...Object.keys(updatedRecord)])

    // Check each key
    for (const key of allKeys) {
        // Skip keys that start with _
        if (key.startsWith('_')) continue

        // If values are different, include in diff
        if (!isEqual(originalRecord[key], updatedRecord[key])) {
            diff[key] = updatedRecord[key]
        }
    }

    return diff
}

function useDereferencedRecords(record?: RecordDto): RecordDto[] {
    const records = record?._dereferenced_records as RecordDto[] | undefined

    // Dereferenced users don't have avatars, so we need to fetch them separately.
    const usersObject = useStackerUsersObject()
    const userRecordSids = records?.reduce((acc, record) => {
        if (isUsersRecord(record, usersObject)) {
            acc.push(record._sid)
        }

        return acc
    }, [] as string[])

    const { records: userRecords } = useUserRecordLinksAvatars(userRecordSids)
    const userRecordsMap = userRecords?.reduce(
        (acc, record) => acc.set(record._sid, record),
        new Map<string, RecordDto>()
    )

    const allRecords = [...(records ?? [])]
    if (!!record) {
        allRecords.push(record)
    }

    const finalDereferencedRecords = allRecords.map((record) => {
        const userOverride = userRecordsMap?.get(record._sid)
        if (userOverride) {
            return userOverride
        }

        return record
    })

    return useDeepEqualsMemoValue(finalDereferencedRecords)
}

function getIncludedFieldsFromString(includedFieldsStr?: string) {
    if (!includedFieldsStr) return undefined

    return includedFieldsStr.split(',').filter(Boolean).sort()
}
